import { localize } from '@/core/gambits';
import { formatUnknownError, logger } from '@/core/helpers/logger.helper';
import {
  canvasToPngBlob,
  drawOverlayLogo,
  drawOverlayWordmark,
  OVERLAY_FONT_FAMILY,
  OVERLAY_LOGO_HEIGHT,
  OVERLAY_WORDMARK_HEIGHT,
} from '@/core/helpers/overlay-brand.helper';
import type { SessionSummaryResponse } from '@/core/network/api/walk/walk.api';
import { ratingBandRange, ratingToBand } from './walk-rating-band';

/* Renders the walk result as a transparent PNG meant to be layered on top of a
   video (Stories, CapCut). Drawn on a canvas rather than captured from the DOM:
   the drawer's own styling is dark-background and oklch based, neither of which
   survives a screenshot onto transparency. */

const WIDTH = 1080;
const PADDING = 72;
const CONTENT_WIDTH = WIDTH - PADDING * 2;

const ROUTE_MIN_HEIGHT = 260;
const ROUTE_MAX_HEIGHT = 720;
const ROUTE_INSET = 40;

const GAP_AFTER_ROUTE = 76;
const CHIP_SIZE = 132;
const GAP_AFTER_CHIP = 60;
const LABEL_HEIGHT = 44;
const GAP_LABEL_TO_VALUE = 12;
const VALUE_HEIGHT = 136;
const GAP_BETWEEN_STATS = 52;
const META_HEIGHT = 46;
const GAP_BEFORE_LOGO = 68;
const GAP_AFTER_LOGO = 26;

const COLOR_ROUTE = 'rgba(255, 255, 255, 0.9)';
const COLOR_CORRECT = '#2ecc71';
const COLOR_INCORRECT = '#e74c3c';
const COLOR_NOT_SUBMITTED = 'rgba(255, 255, 255, 0.25)';

/* Reveal drawer `.squares-shape-wrapper` is 260px wide; squares are 14px with
   a 3px radius. Overlay sizes are scaled from that so the path matches. */
const REVEAL_SHAPE_WIDTH = 260;
const REVEAL_SQUARE_PX = 14;
const REVEAL_SQUARE_RADIUS_PX = 3;
const REVEAL_SQUARE_STROKE_PX = 1.5;
const REVEAL_MARKER_STROKE_PX = 2;
const REVEAL_STREAK_STROKE_PX = 2;

interface RouteProjection {
  aspect: number;
  toX: (lng: number) => number;
  toY: (lat: number) => number;
}

interface RoutePoint {
  x: number;
  y: number;
}

type WalkRound = SessionSummaryResponse['rounds'][number];

/** Normalises coordinates to 0–1 on both axes, mirroring the reveal drawer's projection. */
function projectRoute(summary: SessionSummaryResponse): RouteProjection | null {
  const { originLat, originLng } = summary;
  if (originLat == null || originLng == null) return null;

  const lats: number[] = [originLat];
  const lngs: number[] = [originLng];

  if (summary.revealLat != null && summary.revealLng != null) {
    lats.push(summary.revealLat);
    lngs.push(summary.revealLng);
  }

  for (const round of summary.rounds) {
    if (round.lat != null && round.lng != null) {
      lats.push(round.lat);
      lngs.push(round.lng);
    }
  }

  if (lats.length < 2) return null;

  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);

  const latRange = maxLat - minLat || 0.0001;
  const lngRange = maxLng - minLng || 0.0001;

  return {
    aspect: latRange / lngRange,
    toX: (lng: number) => (lng - minLng) / lngRange,
    toY: (lat: number) => (maxLat - lat) / latRange,
  };
}

function roundColor(round: WalkRound): string {
  if (!round.submitted) return COLOR_NOT_SUBMITTED;
  if (round.isCorrect) return COLOR_CORRECT;
  return COLOR_INCORRECT;
}

function squareRect(center: RoutePoint, size: number): { x: number; y: number; size: number } {
  return { x: center.x - size / 2, y: center.y - size / 2, size };
}

function fillSquare(
  ctx: CanvasRenderingContext2D,
  center: RoutePoint,
  size: number,
  radius: number,
  color: string,
): void {
  const { x, y } = squareRect(center, size);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.roundRect(x, y, size, size, radius);
  ctx.fill();
}

/** Stroked inside the box, matching the drawer's `inset` box-shadow squares. */
function strokeSquare(
  ctx: CanvasRenderingContext2D,
  center: RoutePoint,
  size: number,
  radius: number,
  lineWidth: number,
  color: string,
): void {
  const { x, y } = squareRect(center, size);
  const inset = lineWidth / 2;
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  ctx.roundRect(x + inset, y + inset, size - lineWidth, size - lineWidth, Math.max(0, radius - inset));
  ctx.stroke();
}

/** Longest consecutive submitted-and-correct run. Same scan as the reveal polyline. */
function longestStreakRange(rounds: WalkRound[]): { start: number; length: number } | null {
  let bestStart = 0;
  let bestLen = 0;
  let curStart = 0;
  let curLen = 0;

  for (let i = 0; i < rounds.length; i++) {
    if (rounds[i].submitted && rounds[i].isCorrect) {
      if (curLen === 0) curStart = i;
      curLen++;
      if (curLen > bestLen) {
        bestLen = curLen;
        bestStart = curStart;
      }
    } else {
      curLen = 0;
    }
  }

  if (bestLen < 2) return null;
  return { start: bestStart, length: bestLen };
}

function drawRoute(
  ctx: CanvasRenderingContext2D,
  summary: SessionSummaryResponse,
  projection: RouteProjection,
  top: number,
  height: number,
): void {
  const areaWidth = CONTENT_WIDTH - ROUTE_INSET * 2;
  const areaHeight = height - ROUTE_INSET * 2;
  const toPoint = (lat: number, lng: number): RoutePoint => ({
    x: PADDING + ROUTE_INSET + projection.toX(lng) * areaWidth,
    y: top + ROUTE_INSET + projection.toY(lat) * areaHeight,
  });

  const scale = areaWidth / REVEAL_SHAPE_WIDTH;
  const squareSize = REVEAL_SQUARE_PX * scale;
  const squareRadius = REVEAL_SQUARE_RADIUS_PX * scale;
  const squareStroke = REVEAL_SQUARE_STROKE_PX * scale;
  const streakWidth = REVEAL_STREAK_STROKE_PX * scale;

  const fallbackCenter: RoutePoint = {
    x: PADDING + ROUTE_INSET + areaWidth / 2,
    y: top + ROUTE_INSET + areaHeight / 2,
  };

  const cells: RoutePoint[] = summary.rounds.map((round) =>
    round.lat != null && round.lng != null ? toPoint(round.lat, round.lng) : fallbackCenter,
  );

  const startPt =
    summary.originLat != null && summary.originLng != null ? toPoint(summary.originLat, summary.originLng) : null;
  const revealPt =
    summary.revealLat != null && summary.revealLng != null ? toPoint(summary.revealLat, summary.revealLng) : null;

  for (let i = 0; i < summary.rounds.length; i++) {
    const round = summary.rounds[i];
    const center = cells[i];
    if (!round.submitted) {
      strokeSquare(ctx, center, squareSize, squareRadius, squareStroke, COLOR_NOT_SUBMITTED);
      continue;
    }
    fillSquare(ctx, center, squareSize, squareRadius, roundColor(round));
    if (!round.isCorrect && round.redWalkIsCorrect === true) {
      ctx.fillStyle = COLOR_CORRECT;
      ctx.beginPath();
      ctx.arc(center.x, center.y, squareSize * 0.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  const streak = longestStreakRange(summary.rounds);
  if (streak) {
    const points = cells.slice(streak.start, streak.start + streak.length);
    ctx.strokeStyle = COLOR_ROUTE;
    ctx.lineWidth = streakWidth;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (const point of points.slice(1)) {
      ctx.lineTo(point.x, point.y);
    }
    ctx.stroke();
  }

  // Markers sit on top of the squares and the streak line, as in the drawer.
  if (startPt) {
    strokeSquare(ctx, startPt, squareSize, squareRadius, REVEAL_MARKER_STROKE_PX * scale, '#ffffff');
  }

  if (revealPt) {
    fillSquare(ctx, revealPt, squareSize, squareRadius, '#ffffff');
  }
}

function drawChip(ctx: CanvasRenderingContext2D, label: string, top: number): void {
  const left = WIDTH / 2 - CHIP_SIZE / 2;

  ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
  ctx.fillRect(left, top, CHIP_SIZE, CHIP_SIZE);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.lineWidth = 4;
  ctx.strokeRect(left, top, CHIP_SIZE, CHIP_SIZE);

  ctx.fillStyle = '#ffffff';
  ctx.font = `700 60px ${OVERLAY_FONT_FAMILY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, WIDTH / 2, top + CHIP_SIZE / 2 + 2);
}

function drawLabel(ctx: CanvasRenderingContext2D, text: string, top: number): void {
  ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
  ctx.font = `600 34px ${OVERLAY_FONT_FAMILY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text.toUpperCase(), WIDTH / 2, top + LABEL_HEIGHT / 2);
}

function accuracyPercentage(summary: SessionSummaryResponse): string {
  const total = summary.rounds.length;
  if (total === 0) return '-';
  return `${Math.round((summary.totalSolved / total) * 100)}%`;
}

function fieldLabel(summary: SessionSummaryResponse): string {
  const speedKmh = Math.round(summary.breakEvenSpeedMps * 3.6);
  return `${ratingToBand(summary.puzzleRating).toUpperCase()}${speedKmh}`;
}

function metaLabel(summary: SessionSummaryResponse): string {
  const speedKmh = Math.round(summary.breakEvenSpeedMps * 3.6);
  return `${ratingBandRange(summary.puzzleRating)} · ${speedKmh}km/h`;
}

/**
 * Builds a transparent-background PNG with the route, field chip, accuracy and
 * field range. Resolves to `null` when rendering is not possible — callers are
 * expected to surface that, never to retry.
 */
export async function buildWalkOverlayPng(summary: SessionSummaryResponse): Promise<Blob | null> {
  try {
    // Without the app font the labels fall back mid-draw and the layout shifts.
    await document.fonts?.ready;

    const projection = projectRoute(summary);
    const routeHeight = projection
      ? Math.max(ROUTE_MIN_HEIGHT, Math.min(ROUTE_MAX_HEIGHT, projection.aspect * CONTENT_WIDTH))
      : 0;

    const canvas = document.createElement('canvas');
    canvas.width = WIDTH;
    canvas.height =
      PADDING * 2 +
      (projection ? routeHeight + GAP_AFTER_ROUTE : 0) +
      CHIP_SIZE +
      GAP_AFTER_CHIP +
      // Accuracy block
      LABEL_HEIGHT +
      GAP_LABEL_TO_VALUE +
      VALUE_HEIGHT +
      GAP_BETWEEN_STATS +
      // Rating · speed block
      LABEL_HEIGHT +
      GAP_LABEL_TO_VALUE +
      META_HEIGHT +
      GAP_BEFORE_LOGO +
      OVERLAY_LOGO_HEIGHT +
      GAP_AFTER_LOGO +
      OVERLAY_WORDMARK_HEIGHT;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Keeps white type and route readable over bright footage.
    ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
    ctx.shadowBlur = 18;
    ctx.shadowOffsetY = 4;

    let cursor = PADDING;

    if (projection) {
      drawRoute(ctx, summary, projection, cursor, routeHeight);
      cursor += routeHeight + GAP_AFTER_ROUTE;
    }

    drawChip(ctx, fieldLabel(summary), cursor);
    cursor += CHIP_SIZE + GAP_AFTER_CHIP;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    drawLabel(ctx, localize('walk.reveal.accuracy'), cursor);
    cursor += LABEL_HEIGHT + GAP_LABEL_TO_VALUE;

    ctx.fillStyle = '#ffffff';
    ctx.font = `700 128px ${OVERLAY_FONT_FAMILY}`;
    ctx.fillText(accuracyPercentage(summary), WIDTH / 2, cursor + VALUE_HEIGHT / 2);
    cursor += VALUE_HEIGHT + GAP_BETWEEN_STATS;

    drawLabel(ctx, `${localize('walk.reveal.rating')} · ${localize('walk.reveal.speed')}`, cursor);
    cursor += LABEL_HEIGHT + GAP_LABEL_TO_VALUE;

    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.font = `500 40px ${OVERLAY_FONT_FAMILY}`;
    ctx.fillText(metaLabel(summary), WIDTH / 2, cursor + META_HEIGHT / 2);
    cursor += META_HEIGHT + GAP_BEFORE_LOGO;

    drawOverlayLogo(ctx, WIDTH / 2, cursor);
    cursor += OVERLAY_LOGO_HEIGHT + GAP_AFTER_LOGO;

    drawOverlayWordmark(ctx, WIDTH / 2, cursor);

    return await canvasToPngBlob(canvas);
  } catch (error) {
    logger.error(`Walk overlay render failed: ${formatUnknownError(error)}`);
    return null;
  }
}
